// A React component-based site structure for The Garbage Brigade
// Framework: React with Tailwind CSS for styling and React Router for routing

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useParams } from 'react-router-dom';
import BioCard from './BioCard';
import highlights from './highlights';

const garbageBrigade = [
  { name: 'Malik Thompson', alias: 'ThompSmash', role: 'Hype Host + FPS Specialist', interests: 'Apex Legends, Call of Duty, rage-quitting with style', image: '/images/malikthompson.png', socials: { twitch: 'https://twitch.tv/ThompSmash', twitter: 'https://twitter.com/ThompSmash' } },
  { name: 'Carmen Ruiz', alias: 'BigBrainCarm', role: 'Puzzle Queen + Strategy Stream Lead', interests: 'Portal, Civilization VI, brainy co-op games', image: '/images/carmenruiz.png', socials: { twitch: 'https://twitch.tv/BigBrainCarm' } },
  { name: 'Kenji Tanaka', alias: 'PingLord', role: 'Support Main + Vibes Manager', interests: 'Overwatch, indie pixel RPGs, cozy farming sims', image: '/images/kenjitanaka.png', socials: { twitter: 'https://twitter.com/PingLord' } },
  { name: 'Zuri Wells', alias: 'Z-Wreck', role: 'Chaos Agent + Fighting Game Guru', interests: 'Smash Bros., Tekken, pushing every button to see what happens', image: '/images/zuriwells.png', socials: {} },
  { name: 'Elliot Novak', alias: 'GlitchDad', role: 'Tech Lead + Retro Arcade Archivist', interests: 'Emulator mods, JRPGs, anything with a ‘98 release date', image: '/images/elliotnovak.png', socials: { youtube: 'https://youtube.com/GlitchDad' } },
  { name: 'Marianne Keller', alias: 'MamaSalt', role: 'Team Mom + Story-Heavy Game Streamer', interests: 'Narrative adventures, visual novels, tactical RPGs', image: '/images/mariannekeller.png', socials: { twitch: 'https://twitch.tv/MamaSalt', twitter: 'https://twitter.com/MamaSalt' } }
];

const moderators = [
  { name: 'Jules Starling', alias: 'ModMama', role: 'Head Mod + Chat Wrangler', interests: 'Banning bots, reining in chaos, making clip compilations', image: '/images/julesstarling.png', socials: { twitter: 'https://twitter.com/ModMama' } },
  { name: 'Oscar Lin', alias: 'ModSquash', role: 'Tech Mod + Meme Archivist', interests: 'Server tweaking, nightbot scripts, cursed emoji', image: '/images/oscarlin.png', socials: { github: 'https://github.com/ModSquash' } }
];
function ProfilePage() {
  const { alias } = useParams();
  const allMembers = [...garbageBrigade, ...moderators];
  const person = allMembers.find(p => p.alias === alias);

  if (!person) {
    return <div className="text-white p-10 text-center">No such profile found. Please return to the homepage.</div>;
  }

  return (
    <div className="bg-gray-900 text-white font-sans min-h-screen px-6 py-20 max-w-3xl mx-auto">
      <div className="flex flex-col items-center space-y-6">
        <img src={person.image} alt={person.name} className="rounded-xl w-64 h-64 object-cover shadow-lg" />
        <h1 className="text-4xl font-bold">{person.name}</h1>
        <h2 className="text-xl text-indigo-400">{person.role}</h2>
        <p className="text-gray-300 text-center max-w-xl">
          {person.name === 'Jules Starling' ? (
            <>Jules keeps our stream chats clean and our community happier than a golden retriever at a dog park. Known as ModMama, she leads our moderator crew with quick reflexes, witty comebacks, and an uncanny ability to detect trolls before they even press enter. She's the reason we're allowed to be unhinged — because she's the hinged one behind the curtain.</>
          ) : person.name === 'Oscar Lin' ? (
            <>Oscar is the behind-the-scenes wizard who keeps our servers in order and our chat memes spicy. Whether he's fine-tuning bot permissions or deploying a new joke command, he's the reason our viewers are laughing and our infrastructure hasn't collapsed into flaming bits. If Jules wields the banhammer, Oscar wields the emoji cannon.</>
          ) : person.name === 'Malik Thompson' ? (
            <>Malik, a.k.a. ThompSmash, is the heartbeat of the Garbage Brigade — both in volume and hype. Whether he's shoutcasting an FPS takedown or initiating pure chaos in Apex, he's always the first to jump into danger (and the last to admit he fell off a cliff by accident). He's loud, he's proud, and he's definitely not muting his mic.</>
          ) : person.name === 'Carmen Ruiz' ? (
            <>Carmen is the strategist of the squad. She'll figure out the puzzle in Portal before you’ve even finished reading the objective. A natural leader and lore whisperer, she turns slow-burn games into high-octane intellectual marathons with just a raised eyebrow and a sarcastic quip.</>
          ) : person.name === 'Elliot Novak' ? (
            <>GlitchDad might look like your neighbor who fixes old VCRs, but he’s the wizard of retro gaming and emulation setups. If you’ve got questions about weird Japanese exclusives from 1998, Elliot’s your guy. His streams feel like LAN parties from a parallel timeline where nostalgia is currency.</>
          ) : person.name === 'Marianne Keller' ? (
            <>MamaSalt’s streams are like emotional roller coasters — sometimes soft, sometimes salty, always deeply felt. As the team’s emotional anchor, she draws in fans with her love of storytelling and ability to make you cry from laughter or plot twists. Also, she makes the best cookies. Allegedly.</>
          ) : person.name === 'Kenji Tanaka' ? (
            <>Kenji is calm under pressure, unshakeable in Overwatch, and responsible for 90% of the good vibes. He might not say much, but when he does, it’s either devastatingly wholesome or absurdly hilarious. He’s the heart of the support squad — both on-screen and in our DMs.</>
          ) : person.name === 'Zuri Wells' ? (
            <>Zuri brings the fire, the chaos, and the joystick fury. A total button masher with mind-blowing instincts, she’s our wildcard and arcade brawler queen. She doesn’t do things halfway — she does them loudly, dramatically, and with glitter.</>
          ) : (
            <>This team member's full bio is still being written, but trust us — they bring the sauce, the spice, and usually a backup USB stick just in case. Check back soon for more!</>
          )}
        </p>
        {person.socials && Object.keys(person.socials).length > 0 && (
          <div className="mt-6 space-x-4">
            {Object.entries(person.socials).map(([platform, url]) => (
              <a key={platform} href={url} target="_blank" rel="noopener noreferrer" className="inline-block bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-full text-sm capitalize">
                {platform}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function HomePage() {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-gray-900 text-white font-sans">
      <header className="sticky top-0 bg-gray-950/90 backdrop-blur-sm z-50 border-b border-gray-700">
        <nav className="flex justify-center space-x-8 py-4 text-sm font-semibold text-gray-300">
          <button onClick={() => scrollToSection('intro')} className="hover:text-white transition-colors duration-300">Intro</button>
          <button onClick={() => scrollToSection('brigade')} className="hover:text-white transition-colors duration-300">Team</button>
          <Link to="/highlights" className="hover:text-white transition-colors duration-300">Highlights</Link>
        </nav>
      </header>

      <section id="intro" className="text-center py-20 px-6 max-w-4xl mx-auto">
        <h1 className="text-5xl font-extrabold mb-4">The Garbage Brigade</h1>
        <p className="text-lg text-gray-300 leading-relaxed space-y-3">
          The Garbage Brigade is where controlled chaos meets competitive spirit. We're a streaming alliance of gamers,
          creators, and misfits who might not have the best aim, but we absolutely have the best energy. Each member
          brings their own spice to the stew — whether it’s unhinged ragequits, tear-jerking story playthroughs,
          cracked aim, or totally unnecessary soundboards.
        </p>
        <p className="text-lg text-gray-300 leading-relaxed mt-4">
          We stream separately across Twitch, YouTube, and haunted frequencies only heard by raccoons — but this is
          the site that brings us together. Here you'll find team bios, our favorite stream clips, and a spot for
          the unsung heroes: our moderators. Because behind every trash fire is someone holding a fire extinguisher
          and a banhammer.
        </p>
      </section>

      <section id="brigade" className="py-16 px-6">
        <h2 className="text-4xl font-bold text-center mb-12">Actual Garbage</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10 justify-items-center">
          {garbageBrigade.map((member, i) => (
            <Link to={`/profile/${member.alias}`} key={i} className="transition-transform duration-500 hover:scale-105">
              <BioCard {...member} />
            </Link>
          ))}
        </div>

        <h3 className="text-3xl font-semibold text-center mt-16 mb-8">Our Moderators</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 justify-items-center">
          {moderators.map((mod, i) => (
            <Link to={`/profile/${mod.alias}`} key={i} className="transition-transform duration-500 hover:scale-105">
              <BioCard {...mod} />
            </Link>
          ))}
        </div>
      </section>


      <section className="py-16 px-6 bg-gray-800">
        <h2 className="text-4xl font-bold text-center mb-4">The Trash Cannots</h2>
        <p className="text-center text-gray-400 text-lg">
          This secondary squad is still under construction. Check back soon for more madness.
        </p>
      </section>


      <section className="py-16 px-6">
        <h2 className="text-4xl font-bold text-center mb-12">Stream Highlights</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {highlights.slice(0, 3).map((vid, i) => (
            <div key={i} className="rounded-lg overflow-hidden shadow-lg transform transition-transform duration-300 hover:scale-105">
              <iframe
                src={vid.url}
                title={vid.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-64 border-none"
              ></iframe>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/highlights" className="inline-block bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-3 rounded-full">
            View All Highlights
          </Link>
        </div>
      </section>


      <section className="py-16 px-6 bg-indigo-900 text-white text-center">
        <h2 className="text-4xl font-bold mb-4">Join Our Discord</h2>
        <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-6">
          Be part of the chaos off-stream! Join our Discord to chat, meme, and get sneak peeks at upcoming content.
        </p>
        <a
          href="https://discord.gg/YOUR_INVITE_CODE"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block bg-indigo-600 hover:bg-indigo-500 px-6 py-3 rounded-full font-semibold"
        >
          Join the Community
        </a>
      </section>

      <section className="py-10 px-6 bg-gray-950 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-4">Who's Live Now?</h2>
          <p className="text-gray-400 text-sm">Nobody's live at the moment. But that could change in a blink.</p>
          {/* Later enhancement: integrate Twitch API here */}
        </div>
      </section>

      <footer className="text-center text-sm text-gray-500 py-10">
        © 2025 The Garbage Brigade</footer>
    </div>
  );
}

function HighlightsPage() {
  return (
    <div className="bg-gray-900 text-white font-sans min-h-screen py-20 px-6">
      <h2 className="text-4xl font-bold text-center mb-12">Stream Highlights</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {highlights.map((vid, i) => (
          <div key={i} className="rounded-lg overflow-hidden shadow-lg transform transition-transform duration-300 hover:scale-105">
            <iframe
              src={vid.url}
              title={vid.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-64 border-none"
            ></iframe>
          </div>
        ))}
      </div>
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="bg-gray-900 text-white font-sans min-h-screen flex items-center justify-center px-6">
      <div className="text-center">
        <h1 className="text-6xl font-bold mb-4">404</h1>
        <p className="text-xl text-gray-300 mb-6">Oops! The page you're looking for doesn't exist.</p>
        <Link to="/" className="inline-block bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-3 rounded-full">Back to Home</Link>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/highlights" element={<HighlightsPage />} />
        <Route path="/profile/:alias" element={<ProfilePage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Router>
  );
}

export default App;
